import { useState } from "react"
import { CheckCircleIcon } from "@heroicons/react/24/outline"
import { SubmitSupportTicket } from "aldoo-ra/Admin/CustomerSupport/api"
import Spinner from "aldoo-ra/Spinner"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import Typography from "aldoo-ra/Typography"
import Modal from "aldoo-ra/Modal"
import Input from "aldoo-ra/Input"
import Button from "aldoo-ra/Button"
import TextBundle from "aldoo-ra/CMS/text-bundle"

export default function ContactDialog({ onClose, initialCategory = "" }) {
  const texts = TextBundle({ id: "transfer_contact_dialog" })

  const CATEGORIES = [
    { value: "sales", label: texts?.salesLabel, description: texts?.salesDescription },
    { value: "issue", label: texts?.issueLabel, description: texts?.issueDescription },
    { value: "feature", label: texts?.featureLabel, description: texts?.featureDescription },
  ]

  const [category, setCategory] = useState(initialCategory)
  const [form, setForm] = useState({ name: "", email: "", message: "" })
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState("")

  const handleChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }))

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!category) return setError(texts?.noCategoryError)
    setError("")
    setSubmitting(true)
    try {
      const res = await SubmitSupportTicket({
        email: form.email,
        summary: `${category.charAt(0).toUpperCase() + category.slice(1)}: ${form.name || form.email}`,
        description: form.message,
        tags: [category],
      })
      if (res?.result) {
        setSubmitted(true)
      } else {
        setError(res?.message || texts?.genericError)
      }
    } catch {
      setError(texts?.networkError)
    } finally {
      setSubmitting(false)
    }
  }

  const footer = !submitted ? (
    <div className="w-full">
      {error && <p className="text-sm text-error mb-3">
                  {error}
                </p>
      }

      <Button
        type="button"
        variant="primary"
        onClick={(e) => { e.preventDefault(); document.getElementById("contact-form")?.requestSubmit() }}
        disabled={submitting}
        className="w-full"
      >
        {submitting && <Spinner className="w-4 h-4" />}
        {texts?.sendButton}
      </Button>
    </div>
  ) : null

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={texts?.dialogTitle}
      variant="pulse_compact"
      footerContent={footer}
      styleOverrides={{
        container: "relative w-full h-full md:h-auto md:max-w-[450px] md:max-h-[90vh] bg-canvas md:rounded-2xl shadow-xl flex flex-col",
      }}
    >
      {submitted ? (
        <div className="flex flex-col items-center text-center py-6 gap-4 px-5">
          <CheckCircleIcon className="w-14 h-14 text-success" />

          <Typography 
            variant="h5"
            className="my-0"
          >
            {texts?.messageSentTitle}
          </Typography>

          <Typography variant="txt1_dim">
            {texts?.messageSentSubtitle}
          </Typography>

          <Button
            variant="primary"
            onClick={onClose}
          >
            {texts?.doneButton}
          </Button>
        </div>
      ) : (
        <form 
          id="contact-form" 
          onSubmit={handleSubmit} 
          className="flex flex-col gap-5 px-5 py-5"
        >
          {/* Category */}
          <div className="flex flex-col">
            <Typography variant="label_accent">
              {texts?.categoryLabel}
            </Typography>

            <DropdownSelect
              variant="pulse_compact"
              options={CATEGORIES}
              value={category ? [CATEGORIES.find((c) => c.value === category)] : []}
              valueField="value"
              placeholder={texts?.categorySelectorPlaceholder}
              showCheckbox={false}
              showSelectAll={false}
              enableResetButton={false}
              allowEmptySelection={false}
              onChange={(selected) => setCategory(selected?.[0]?.value || "")}
              renderOption={(option) => (
                <div className="flex items-start gap-2 px-4 py-2 border-b border-bordered last:border-b-0 text-left text-text  transition">
                  <div>
                    <span className="text-sm font-medium block">
                      {option.label}
                    </span>

                    <span className="text-xs text-text_secondary">
                      {option.description}
                    </span>
                  </div>
                </div>
              )}
              onValueItemRender={(option) => option
                ? (
                  <div className="w-full flex items-start gap-3 text-left px-4 py-3">
                    <div>
                      <span className="text-sm font-medium block text-text">
                        {option.label}
                      </span>

                      <span className="text-xs text-text_secondary">
                        {option.description}
                      </span>
                    </div>
                  </div>
                )
                : <span className="text-sm text-text_secondary px-4 py-3">
                    {texts?.categorySelector}
                  </span>
              }
            />
          </div>

          {/* Name + Email */}
          <div className="flex flex-col gap-3">
            <div className="flex flex-col">
              <Typography variant="label_accent">
                {texts?.nameLabel}
              </Typography>

              <Input
                variant="pulse_compact"
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder={texts?.namePlaceholder}
                required
              />
            </div>

            <div className="flex flex-col">
              <Typography variant="label_accent">
                {texts?.emailLabel}
              </Typography>

              <Input
                variant="pulse_compact"
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder={texts?.emailPlaceholder}
                required
              />
            </div>
          </div>

          {/* Message */}
          <div className="flex flex-col">
            <Typography variant="label_accent">
              {texts?.messageLabel}
            </Typography>

            <textarea
              name="message"
              value={form.message}
              onChange={handleChange}
              placeholder={texts?.messagePlaceholder}
              rows={4}
              required
              className="border border-bordered rounded-xl px-3 py-2 text-sm bg-paper text-text focus:outline-none focus:border-primary resize-none"
            />
          </div>
        </form>
      )}
    </Modal>
  )
}
